import React, { useState, useEffect, useRef } from 'react';
import {
  View, Text, Image, TouchableOpacity, ScrollView, StyleSheet, Platform, TextInput, Modal,
} from 'react-native';
import { useRoute, RouteProp } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { FONTS } from '../../constants/config';
import { API_BASE_HOST } from '../../constants/config';
import { createShadow } from '../../utils/styles';
import { PageContainer, Navbar, Breadcrumb, Toast } from '../../components';
import TalentsSkeleton from './components/TalentsSkeleton';
import { activityOutputsApi } from '../../services/apiService';
import { GalleryItem } from './types';
import { MainStackParamList } from '../../navigation/MainNavigator';

type ScreenRouteProp = RouteProp<MainStackParamList, 'OutputGallery'>;

function resolveUrl(src: string | null | undefined): string | null {
  if (!src) return null;
  if (src.startsWith('http')) return src;
  return `${API_BASE_HOST}${src}`;
}

function downloadFile(url: string, filename: string) {
  if (Platform.OS !== 'web') return;
  fetch(url)
    .then(res => res.blob())
    .then(blob => {
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(blobUrl);
    })
    .catch(() => {
      // Fallback: open in new tab
      window.open(url, '_blank');
    });
}

/* ── Preview Modal ──────────────────────────────────────── */
function PreviewModal({ item, visible, onClose }: { item: GalleryItem | null; visible: boolean; onClose: () => void }) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  if (!item) return null;
  const url = resolveUrl(typeof item.image === 'string' ? item.image : null);
  if (!url) return null;

  // Determine the best preview URL based on file type
  const ext = url.split('.').pop()?.split('?')[0]?.toLowerCase() || '';
  const isPdf = ext === 'pdf';
  const isOffice = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext);
  const previewUrl = `${API_BASE_HOST}/api/v1/activity-outputs/${item.id}/preview/`;
  // For office files on public domains, use Google Docs Viewer
  const isPublic = typeof window !== 'undefined' && !window.location.hostname.includes('localhost');
  const officeViewerUrl = isPublic
    ? `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`
    : null;

  const handleDownload = () => downloadFile(url, item.caption || 'file');

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.85)', justifyContent: 'center', alignItems: 'center' }}>
        {/* Top bar */}
        <View style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 20, paddingTop: 16, paddingBottom: 8 }}>
          <TouchableOpacity onPress={onClose} style={{ padding: 8 }}>
            <Ionicons name="close" size={28} color="#FFFFFF" />
          </TouchableOpacity>
          <Text style={{ color: '#FFFFFF', fontSize: 14, fontFamily: FONTS.medium, flex: 1, textAlign: 'center' }} numberOfLines={1}>{item.caption}</Text>
          <TouchableOpacity onPress={handleDownload} style={{ padding: 8 }}>
            <Ionicons name="download-outline" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
        {/* Content */}
        <View style={{ width: '92%', height: '80%', marginTop: 50 }}>
          {item.type === 'video' ? (
            Platform.OS === 'web' ? (
              <video src={url} controls autoPlay style={{ width: '100%', height: '100%', borderRadius: 8, objectFit: 'contain' } as any} />
            ) : null
          ) : item.type === 'file' ? (
            Platform.OS === 'web' ? (
              isPdf ? (
                <iframe src={previewUrl} style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8, backgroundColor: '#fff' } as any} />
              ) : isOffice && officeViewerUrl ? (
                <iframe src={officeViewerUrl} style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8, backgroundColor: '#fff' } as any} />
              ) : (
                <iframe src={previewUrl} style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8, backgroundColor: '#fff' } as any} />
              )
            ) : null
          ) : (
            <Image source={{ uri: url }} style={{ width: '100%', height: '100%', borderRadius: 8 } as any} resizeMode="contain" />
          )}
        </View>
      </View>
    </Modal>
  );
}

/* ── Folder Item (file card inside a folder) ──────────────── */
function FolderItemCard({ item, onDelete, onPreview, readOnly }: { item: GalleryItem; onDelete?: () => void; onPreview?: () => void; readOnly?: boolean }) {
  const { colors } = useTheme();
  const imgSrc = resolveUrl(typeof item.image === 'string' ? item.image : null);
  const isFile = item.type === 'file';

  return (
    <View style={[cardStyles.container, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
      <TouchableOpacity style={cardStyles.imageWrap} onPress={onPreview} activeOpacity={0.7}>
        {imgSrc && !isFile ? (
          <Image source={{ uri: imgSrc }} style={cardStyles.image} resizeMode="cover" />
        ) : (
          <View style={[cardStyles.image, { backgroundColor: '#E2E8F0', alignItems: 'center', justifyContent: 'center' }]}>
            <Ionicons
              name={item.type === 'video' ? 'videocam-outline' : isFile ? 'document-text-outline' : 'image-outline'}
              size={40}
              color="#94A3B8"
            />
          </View>
        )}
        {item.type === 'video' && (
          <View style={cardStyles.playOverlay}>
            <Ionicons name="play-circle" size={48} color="rgba(255,255,255,0.9)" />
          </View>
        )}
      </TouchableOpacity>
      <Text style={[cardStyles.caption, { color: colors.textPrimary }]} numberOfLines={2}>
        {item.caption}
      </Text>
      <View style={cardStyles.footerRow}>
        {!readOnly && onDelete && (
          <TouchableOpacity onPress={onDelete}>
            <Ionicons name="trash-outline" size={16} color="#DC2626" />
          </TouchableOpacity>
        )}
        <View style={{ flex: 1 }} />
        {imgSrc && (
          <TouchableOpacity onPress={() => downloadFile(imgSrc, item.caption || 'file')}>
            <Ionicons name="download-outline" size={14} color={colors.textPrimary} />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

/* ── Folder Section ──────────────────────────────────────── */
interface FolderData {
  name: string;
  items: GalleryItem[];
}

function FolderSection({
  folder,
  projectId,
  onRename,
  onDelete,
  onItemAdded,
  onItemDeleted,
  showToast,
  readOnly,
}: {
  folder: FolderData;
  projectId?: number;
  onRename: (newName: string) => void;
  onDelete?: () => void;
  onItemAdded: (item: GalleryItem) => void;
  onItemDeleted: (id: number) => void;
  showToast: (msg: string, type: 'success' | 'error' | 'info') => void;
  readOnly?: boolean;
}) {
  const { colors } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [collapsed, setCollapsed] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(folder.name);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<number | null>(null);
  const [previewItem, setPreviewItem] = useState<GalleryItem | null>(null);

  const handlePreview = (item: GalleryItem) => {
    // For files that can't be previewed inline, open in new tab (OS handles with native app)
    if (item.type === 'file' && Platform.OS === 'web') {
      const itemUrl = resolveUrl(typeof item.image === 'string' ? item.image : null);
      if (itemUrl) {
        const ext = itemUrl.split('.').pop()?.split('?')[0]?.toLowerCase() || '';
        const isPdf = ext === 'pdf';
        const isPublic = !window.location.hostname.includes('localhost');
        // PDF can always be previewed inline; office files only on public domains via Google Docs
        if (!isPdf && !isPublic) {
          window.open(`${API_BASE_HOST}/api/v1/activity-outputs/${item.id}/preview/`, '_blank');
          return;
        }
      }
    }
    setPreviewItem(item);
  };

  const handleFileChange = async (e: any) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !projectId) return;
    setUploading(true);
    for (let i = 0; i < files.length; i++) {
      try {
        const f = files[i];
        const ext = f.name.split('.').pop()?.toLowerCase() || '';
        let outputType = 'image';
        if (['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(ext)) outputType = 'video';
        else if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'zip'].includes(ext)) outputType = 'file';

        const formData = new FormData();
        formData.append('file', f);
        formData.append('project', String(projectId));
        formData.append('title', f.name.replace(/\.[^.]+$/, ''));
        formData.append('output_type', outputType);
        formData.append('folder', folder.name);
        const created = await activityOutputsApi.create(formData);
        onItemAdded({
          id: created.id,
          image: created.file || created.thumbnail || null,
          caption: created.title || f.name,
          type: outputType as 'image' | 'video' | 'file',
        });
      } catch (err) {
        console.log(`[FolderSection] upload failed:`, err);
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    showToast(
      isRTL ? 'تم رفع الملفات بنجاح' : 'Files uploaded successfully',
      'success',
    );
  };

  const handleDeleteItem = (id: number) => {
    setDeleteItemId(id);
  };

  const confirmDeleteItem = async () => {
    if (deleteItemId === null) return;
    try {
      await activityOutputsApi.delete(deleteItemId);
      onItemDeleted(deleteItemId);
      showToast(isRTL ? 'تم حذف العنصر' : 'Item deleted', 'success');
    } catch (err) {
      console.log('[FolderSection] delete failed:', err);
      showToast(isRTL ? 'فشل في الحذف' : 'Delete failed', 'error');
    }
    setDeleteItemId(null);
  };

  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (Platform.OS !== 'web' || folder.items.length === 0) return;
    setExporting(true);
    try {
      // Dynamic import JSZip
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      const folderZip = zip.folder(folder.name) || zip;
      let count = 0;

      for (const item of folder.items) {
        const url = resolveUrl(typeof item.image === 'string' ? item.image : null);
        if (!url) continue;
        try {
          const resp = await fetch(url);
          const blob = await resp.blob();
          const ext = url.split('.').pop()?.split('?')[0] || 'bin';
          const fileName = `${item.caption || `file_${count + 1}`}.${ext}`;
          folderZip.file(fileName, blob);
          count++;
        } catch { /* skip failed downloads */ }
      }

      if (count > 0) {
        const content = await zip.generateAsync({ type: 'blob' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(content);
        a.download = `${folder.name}.zip`;
        a.click();
        URL.revokeObjectURL(a.href);
        showToast(isRTL ? `تم تصدير ${count} ملفات` : `Exported ${count} files`, 'success');
      } else {
        showToast(isRTL ? 'لا توجد ملفات للتصدير' : 'No files to export', 'info');
      }
    } catch (err) {
      console.log('[FolderSection] export zip failed:', err);
      // Fallback: download files individually
      folder.items.forEach(item => {
        const url = resolveUrl(typeof item.image === 'string' ? item.image : null);
        if (url) {
          const a = document.createElement('a');
          a.href = url;
          a.download = item.caption || 'file';
          a.target = '_blank';
          a.click();
        }
      });
    }
    setExporting(false);
  };

  const handleRenameSubmit = () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== folder.name) {
      onRename(trimmed);
    }
    setEditing(false);
  };

  const rows: GalleryItem[][] = [];
  for (let i = 0; i < folder.items.length; i += 3) {
    rows.push(folder.items.slice(i, i + 3));
  }

  return (
    <View style={[sectionStyles.container, { backgroundColor: colors.card, borderColor: colors.cardBorder }, createShadow(0, 1, 3, '#000', 0.1)]}>
      {/* Header */}
      <View style={sectionStyles.header}>
        <TouchableOpacity style={[sectionStyles.toggleBtn, viewMode === 'grid' && { backgroundColor: '#f1f5f9' }]} onPress={() => setViewMode('grid')}>
          <Ionicons name="grid-outline" size={16} color={colors.textPrimary} />
        </TouchableOpacity>
        <TouchableOpacity style={[sectionStyles.toggleBtn, viewMode === 'list' && { backgroundColor: '#f1f5f9' }]} onPress={() => setViewMode('list')}>
          <Ionicons name="list-outline" size={16} color={colors.textPrimary} />
        </TouchableOpacity>

        {/* Add button — hidden for readOnly (strategy team) */}
        {!readOnly && (Platform.OS === 'web' && projectId ? (
          <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #CBD5E1', borderRadius: 8, paddingLeft: 12, paddingRight: 12, paddingTop: 6, paddingBottom: 6, cursor: 'pointer', opacity: uploading ? 0.5 : 1 }}>
            <input
              ref={fileInputRef as any}
              type="file"
              accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip"
              multiple
              onChange={handleFileChange}
              disabled={uploading}
              style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', zIndex: 1 }}
            />
            <span style={{ fontSize: 14, fontFamily: FONTS.regular, color: colors.textPrimary, pointerEvents: 'none' }}>
              {uploading ? (isRTL ? 'جاري الرفع...' : 'Uploading...') : (isRTL ? 'إضافة' : 'Add')}
            </span>
            <Ionicons name="add" size={14} color={colors.textPrimary} />
          </div>
        ) : (
          <TouchableOpacity style={[sectionStyles.btn, { borderColor: '#CBD5E1' }]}>
            <Text style={[sectionStyles.btnText, { color: colors.textPrimary }]}>{isRTL ? 'إضافة' : 'Add'}</Text>
            <Ionicons name="add" size={14} color={colors.textPrimary} />
          </TouchableOpacity>
        ))}

        {/* Export button */}
        <TouchableOpacity style={[sectionStyles.btn, { borderColor: '#CBD5E1', opacity: exporting ? 0.5 : 1 }]} onPress={handleExport} disabled={exporting}>
          <Text style={[sectionStyles.btnText, { color: colors.textPrimary }]}>{exporting ? (isRTL ? 'جاري التصدير...' : 'Exporting...') : (isRTL ? 'تصدير' : 'Export')}</Text>
          <Ionicons name="download-outline" size={14} color={colors.textPrimary} />
        </TouchableOpacity>

        {/* Folder name */}
        <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
          {readOnly ? (
            <Text style={[sectionStyles.title, { color: colors.textPrimary }]}>{folder.name}</Text>
          ) : editing ? (
            <TextInput
              value={editName}
              onChangeText={setEditName}
              onBlur={handleRenameSubmit}
              onSubmitEditing={handleRenameSubmit}
              autoFocus
              style={{
                fontSize: 14, fontFamily: FONTS.regular, textAlign: 'right',
                borderBottomWidth: 1, borderBottomColor: '#A9211F', paddingVertical: 2,
                minWidth: 100, color: colors.textPrimary,
              }}
            />
          ) : (
            <TouchableOpacity onPress={() => { setEditName(folder.name); setEditing(true); }} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
              <Ionicons name="pencil-outline" size={12} color="#94A3B8" />
              <Text style={[sectionStyles.title, { color: colors.textPrimary }]}>{folder.name}</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Delete — hidden for readOnly or last folder */}
        {!readOnly && onDelete && (
          <TouchableOpacity style={sectionStyles.toggleBtn} onPress={onDelete}>
            <Ionicons name="trash-outline" size={14} color="#DC2626" />
          </TouchableOpacity>
        )}
        <TouchableOpacity style={sectionStyles.toggleBtn} onPress={() => setCollapsed(!collapsed)}>
          <Ionicons name={collapsed ? 'chevron-down' : 'expand-outline'} size={16} color={colors.textPrimary} />
        </TouchableOpacity>
      </View>

      {/* Content */}
      {!collapsed && (
        <View style={[sectionStyles.grid, { backgroundColor: colors.backgroundAlt || '#F1F5F9' }]}>
          {folder.items.length === 0 ? (
            <Text style={{ fontSize: 13, fontFamily: FONTS.regular, color: '#94a3b8', textAlign: 'center', paddingVertical: 20, width: '100%' }}>
              {isRTL ? 'لا توجد عناصر' : 'No items'}
            </Text>
          ) : viewMode === 'grid' ? (
            rows.map((row, rowIdx) => (
              <View key={rowIdx} style={sectionStyles.gridRow}>
                {row.map((item) => (
                  <FolderItemCard key={item.id} item={item} onDelete={() => handleDeleteItem(item.id)} onPreview={() => handlePreview(item)} readOnly={readOnly} />
                ))}
                {row.length < 3 && Array.from({ length: 3 - row.length }).map((_, i) => (
                  <View key={`empty-${i}`} style={{ flex: 1 }} />
                ))}
              </View>
            ))
          ) : (
            folder.items.map((item) => {
              const itemUrl = resolveUrl(typeof item.image === 'string' ? item.image : null);
              return (
                <View key={item.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' }}>
                  {!readOnly && (
                    <TouchableOpacity onPress={() => handleDeleteItem(item.id)}>
                      <Ionicons name="trash-outline" size={16} color="#DC2626" />
                    </TouchableOpacity>
                  )}
                  {itemUrl && (
                    <TouchableOpacity onPress={() => {
                      if (itemUrl) downloadFile(itemUrl, item.caption || 'file');
                    }}>
                      <Ionicons name="download-outline" size={16} color={colors.textPrimary} />
                    </TouchableOpacity>
                  )}
                  <TouchableOpacity style={{ flex: 1 }} onPress={() => handlePreview(item)}>
                    <Text style={{ fontSize: 14, fontFamily: FONTS.regular, textAlign: 'right', color: colors.textPrimary }}>{item.caption}</Text>
                  </TouchableOpacity>
                  {item.type === 'file' ? (
                    <Ionicons name="document-text-outline" size={24} color="#94A3B8" />
                  ) : (
                    <View style={{ width: 48, height: 48, borderRadius: 4, overflow: 'hidden', backgroundColor: '#e2e8f0' }}>
                      {itemUrl ? (
                        <Image source={{ uri: itemUrl }} style={{ width: 48, height: 48 }} resizeMode="cover" />
                      ) : (
                        <View style={{ width: 48, height: 48, alignItems: 'center', justifyContent: 'center' }}>
                          <Ionicons name="image-outline" size={20} color="#94A3B8" />
                        </View>
                      )}
                    </View>
                  )}
                </View>
              );
            })
          )}
        </View>
      )}
      <Modal visible={deleteItemId !== null} transparent animationType="fade" onRequestClose={() => setDeleteItemId(null)}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ backgroundColor: colors.card, borderRadius: 12, padding: 24, width: 340, alignItems: 'center' }}>
            <Text style={{ fontSize: 16, fontFamily: FONTS.medium, color: colors.textPrimary, marginBottom: 12 }}>
              {isRTL ? 'تأكيد الحذف' : 'Confirm Delete'}
            </Text>
            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: colors.textSecondary, textAlign: 'center', marginBottom: 20 }}>
              {isRTL ? 'هل أنت متأكد من حذف هذا العنصر؟' : 'Are you sure you want to delete this item?'}
            </Text>
            <View style={{ flexDirection: 'row', gap: 12, width: '100%' }}>
              <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center' }} onPress={() => setDeleteItemId(null)}>
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#414042' }}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: '#DC2626', alignItems: 'center' }} onPress={confirmDeleteItem}>
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'حذف' : 'Delete'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      <PreviewModal item={previewItem} visible={previewItem !== null} onClose={() => setPreviewItem(null)} />
    </View>
  );
}

/* ── Main Screen ─────────────────────────────────────────── */
export default function OutputGalleryScreen() {
  const route = useRoute<ScreenRouteProp>();
  const { t, language } = useLanguage();
  const { colors } = useTheme();
  const { user } = useAuth();
  const isRTL = language === 'ar';
  const { isMobile } = useResponsive();
  const containerPadding = isMobile ? 12 : 24;
  const isStrategyTeam = user?.profile?.user_type === 'strategy_team' || user?.profile?.user_type === 'superuser';
  const isCoordinator = user?.profile?.user_type === 'coordinator';
  const isStudent = user?.profile?.user_type === 'student';
  const galleryReadOnly = isStrategyTeam || isCoordinator || isStudent;

  const outputIdParam = route.params?.id;
  const outputName = route.params?.name ? decodeURIComponent(route.params.name) : '';
  const [outputId, setOutputId] = useState<number | undefined>(outputIdParam);

  const [isLoading, setIsLoading] = useState(true);
  const [toastMsg, setToastMsg] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('info');
  const [title, setTitle] = useState('');
  const [heroImage, setHeroImage] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<number | undefined>();
  const [folders, setFolders] = useState<FolderData[]>([]);
  const coverInputRef = useRef<HTMLInputElement | null>(null);

  const showToast = (msg: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToastType(type);
    setToastMsg(msg);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      let resolvedId = outputId;
      if (!resolvedId && outputName) {
        try {
          const list = await activityOutputsApi.list();
          const results = list?.results || list;
          if (Array.isArray(results)) {
            const match = results.find((o: any) => o.title === outputName);
            if (match) { resolvedId = match.id; setOutputId(match.id); }
          }
        } catch (e) { console.log('[OutputGallery] resolve name failed:', e); }
      }
      if (!resolvedId) { setIsLoading(false); return; }

      try {
        const output = await activityOutputsApi.get(resolvedId);
        if (output) {
          const pid = output.project;
          setProjectId(pid);
          setTitle(output.title || outputName || '');
          setHeroImage(resolveUrl(output.thumbnail || output.file));

          // Fetch all items for this project (excluding the album itself)
          const resp = pid ? await activityOutputsApi.list({ project: pid }) : null;
          const results = resp?.results || resp || [];

          // Group items by folder name
          const folderMap = new Map<string, GalleryItem[]>();

          // Pre-populate from album's folder_names (so empty folders show)
          const albumFolderNames: string[] = output.folder_names || [];
          albumFolderNames.forEach((fn: string) => {
            if (fn && !folderMap.has(fn)) folderMap.set(fn, []);
          });

          if (Array.isArray(results)) {
            results.forEach((item: any) => {
              if (item.id === resolvedId) return; // skip the album record
              if (item.output_type === 'album') return;
              if (!item.folder) return; // skip items without a folder

              const gi: GalleryItem = {
                id: item.id,
                image: item.thumbnail || item.file || null,
                caption: item.title || '',
                type: item.output_type === 'video' ? 'video' : item.output_type === 'file' ? 'file' : 'image',
                studentName: item.student_name || undefined,
              };

              if (!folderMap.has(item.folder)) folderMap.set(item.folder, []);
              folderMap.get(item.folder)!.push(gi);
            });
          }

          // Build folder array preserving order from folder_names
          const orderedFolders: FolderData[] = [];
          folderMap.forEach((items, name) => {
            orderedFolders.push({ name, items });
          });
          setFolders(orderedFolders);
        }
      } catch (e) {
        console.log('[OutputGallery] API failed:', e);
      }
      setIsLoading(false);
    };
    fetchData();
  }, [outputId, outputName]);

  const handleCoverChange = async (e: any) => {
    const file = e.target.files?.[0];
    if (!file || !outputId) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      await activityOutputsApi.update(outputId, formData);
      showToast(isRTL ? 'تم تحديث صورة الغلاف' : 'Cover image updated', 'success');
      const updated = await activityOutputsApi.get(outputId);
      if (updated) setHeroImage(resolveUrl(updated.thumbnail || updated.file));
    } catch (err) {
      console.log('[OutputGallery] cover update failed:', err);
      showToast(isRTL ? 'فشل في تحديث الغلاف' : 'Failed to update cover', 'error');
    }
    if (coverInputRef.current) coverInputRef.current.value = '';
  };

  // Persist folder names back to the album record
  const syncFolderNames = (updatedFolders: FolderData[]) => {
    if (outputId) {
      const names = updatedFolders.map(f => f.name);
      activityOutputsApi.update(outputId, { folder_names: names } as any).catch(() => {});
    }
  };

  const handleAddFolder = () => {
    const name = isRTL ? `مجلد ${folders.length + 1}` : `Folder ${folders.length + 1}`;
    const updated = [...folders, { name, items: [] }];
    setFolders(updated);
    syncFolderNames(updated);
  };

  const handleRenameFolder = (idx: number, newName: string) => {
    const updated = folders.map((f, i) => i === idx ? { ...f, name: newName } : f);
    setFolders(updated);
    syncFolderNames(updated);
    // Update folder name on existing items in backend
    const folder = folders[idx];
    folder.items.forEach(item => {
      activityOutputsApi.update(item.id, { folder: newName } as any).catch(() => {});
    });
  };

  const handleDeleteFolder = (idx: number) => {
    if (folders.length <= 1) return;
    const folder = folders[idx];
    if (folder.items.length > 0) {
      showToast(
        isRTL ? 'لا يمكن حذف مجلد يحتوي على عناصر' : 'Cannot delete folder with items',
        'error',
      );
      return;
    }
    const updated = folders.filter((_, i) => i !== idx);
    setFolders(updated);
    syncFolderNames(updated);
  };

  const handleItemAdded = (folderIdx: number) => (item: GalleryItem) => {
    setFolders(prev => prev.map((f, i) =>
      i === folderIdx ? { ...f, items: [...f.items, item] } : f
    ));
  };

  const handleItemDeleted = (folderIdx: number) => (id: number) => {
    setFolders(prev => prev.map((f, i) =>
      i === folderIdx ? { ...f, items: f.items.filter(it => it.id !== id) } : f
    ));
  };

  return (
    <PageContainer>
      <Navbar />
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.container, { padding: containerPadding }]}>
          <Breadcrumb
            items={[
              { label: t('navigation.home') },
              { label: isRTL ? 'مبادرة مواهب' : 'Talents', route: 'Talents' },
              ...(projectId
                ? [{ label: isRTL ? 'تفاصيل المشروع' : 'Project Details', route: 'ProjectDetail', params: { id: projectId } }]
                : []),
              { label: isRTL ? 'تفاصيل معرض الأعمال' : 'Gallery Details' },
            ]}
          />

          {isLoading ? (
            <TalentsSkeleton />
          ) : (
            <View style={styles.sections}>
              {/* Hero Banner */}
              <View style={[styles.heroCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }, createShadow(0, 1, 3, '#000', 0.1)]}>
                <View style={styles.heroImageWrap}>
                  {heroImage ? (
                    <Image source={{ uri: heroImage }} style={styles.heroImage} resizeMode="cover" />
                  ) : (
                    <View style={[styles.heroImage, { backgroundColor: '#E2E8F0', alignItems: 'center', justifyContent: 'center' }]}>
                      <Ionicons name="image-outline" size={48} color="#94A3B8" />
                    </View>
                  )}
                  <LinearGradient colors={['transparent', 'rgba(0,0,0,0.5)']} style={styles.heroGradient} />
                  <View style={styles.heroOverlay}>
                    {!galleryReadOnly && (Platform.OS === 'web' ? (
                      <div style={{ position: 'relative', cursor: 'pointer' }}>
                        <input ref={coverInputRef as any} type="file" accept="image/png,image/jpeg" onChange={handleCoverChange} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', zIndex: 1 }} />
                        <Ionicons name="create-outline" size={18} color="#fff" />
                      </div>
                    ) : (
                      <TouchableOpacity style={styles.heroEditBtn}>
                        <Ionicons name="create-outline" size={18} color="#fff" />
                      </TouchableOpacity>
                    ))}
                    <Text style={styles.heroTitle}>{title}</Text>
                  </View>
                </View>
              </View>

              {/* Folder Sections */}
              {folders.map((folder, idx) => (
                <FolderSection
                  key={`${idx}-${folder.name}`}
                  folder={folder}
                  projectId={projectId}
                  onRename={(newName) => handleRenameFolder(idx, newName)}
                  onDelete={folders.length > 1 ? () => handleDeleteFolder(idx) : undefined}
                  onItemAdded={handleItemAdded(idx)}
                  onItemDeleted={handleItemDeleted(idx)}
                  showToast={showToast}
                  readOnly={galleryReadOnly}
                />
              ))}

              {/* Add New Folder Button — hidden for strategy team */}
              {!galleryReadOnly && (
                <TouchableOpacity
                  style={[styles.addFolderBtn, { borderColor: '#CBD5E1' }]}
                  onPress={handleAddFolder}
                >
                  <Ionicons name="add" size={16} color={colors.textPrimary} />
                  <Text style={[styles.addFolderBtnText, { color: colors.textPrimary }]}>
                    {isRTL ? 'إضافة مجلد جديد' : 'Add New Folder'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </View>
      </ScrollView>
      <Toast visible={!!toastMsg} message={toastMsg} type={toastType} onHide={() => { setToastMsg(''); setToastType('info'); }} />
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: { flex: 1 },
  scrollContent: { paddingBottom: 120 },
  container: {},
  sections: { gap: 20 },
  heroCard: {
    borderRadius: 8, borderWidth: 1, padding: 20, overflow: 'hidden',
  },
  heroImageWrap: {
    width: '100%', height: 274, borderRadius: 4, overflow: 'hidden', position: 'relative',
  },
  heroImage: {
    width: '100%', height: '100%',
  },
  heroGradient: {
    position: 'absolute', left: 0, right: 0, bottom: 0, height: '60%',
  },
  heroOverlay: {
    position: 'absolute', left: 8, right: 8, bottom: 8,
    flexDirection: 'row', alignItems: 'flex-end', gap: 8, justifyContent: 'flex-end',
  },
  heroEditBtn: { padding: 4 },
  heroTitle: {
    flex: 1, fontSize: 20, fontFamily: FONTS.bold, color: '#fff', textAlign: 'right', lineHeight: 26,
  },
  addFolderBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, borderWidth: 1, borderRadius: 8, paddingVertical: 8, paddingHorizontal: 12,
    minHeight: 40, width: '100%',
  },
  addFolderBtnText: {
    fontSize: 14, fontFamily: FONTS.regular,
  },
});

const sectionStyles = StyleSheet.create({
  container: { borderRadius: 8, borderWidth: 1, padding: 12, gap: 16 },
  header: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  title: { fontSize: 14, fontFamily: FONTS.regular, textAlign: 'right' },
  btn: {
    flexDirection: 'row', alignItems: 'center', gap: 6, borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6,
  },
  btnText: { fontSize: 14, fontFamily: FONTS.regular },
  toggleBtn: {
    width: 32, height: 32, borderWidth: 1, borderColor: '#CBD5E1', borderRadius: 8, alignItems: 'center', justifyContent: 'center',
  },
  grid: { borderRadius: 8, padding: 12, gap: 10 },
  gridRow: { flexDirection: 'row-reverse', gap: 20 },
});

const cardStyles = StyleSheet.create({
  container: { flex: 1, borderRadius: 8, borderWidth: 1, padding: 12, gap: 8 },
  imageWrap: { width: '100%', height: 200, borderRadius: 4, overflow: 'hidden', position: 'relative' },
  image: { width: '100%', height: '100%' },
  playOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.25)',
  },
  caption: { fontSize: 14, fontFamily: FONTS.regular, textAlign: 'right' },
  footerRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
});
